import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import api from "../../api";
import Select from "react-select";

const ClassSubjectAssignPage = () => {
    const [form, setForm] = useState({ class_id: "", section_id: "", subject_ids: [] });
    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [msg, setMsg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editId, setEditId] = useState(null);

    const schoolId = localStorage.getItem("school_id");

    const addURL = `/class-subjects/add`;
    const listURL = `/class-subjects/list`;
    const deleteURL = `/class-subjects/delete`;

    // fetch classes + subjects initially
    useEffect(() => {
        fetchClasses();
        fetchSubjects();
    }, []);

    const fetchClasses = async () => {
        try {
            const res = await api.get("/get-classes");
            if (res.data?.success) setClasses(res.data.data);
            else setClasses([]);
        } catch (err) {
            console.error("Error fetching classes:", err);
            setClasses([]);
        }
    };

    const fetchSections = async (classId) => {
        if (!classId) return;
        try {
            const res = await api.get(`/get-sections/${classId}`);
            if (res.data?.success) setSections(res.data.data);
            else setSections([]);
        } catch (err) {
            console.error("Error fetching sections:", err);
            setSections([]);
        }
    };

    const fetchSubjects = async () => {
        try {
            const res = await api.get(`subjects/list?school_id=${schoolId}`);
            if (res.data?.data) setSubjects(res.data.data);
            else setSubjects([]);
        } catch (err) {
            console.error("Error fetching subjects:", err);
        }
    };

    const fetchAssignments = async (classId, sectionId) => {
        if (!classId) return;
        try {
            const url = `${listURL}?school_id=${schoolId}&class_id=${classId}&section_id=${sectionId || ""}`;
            const res = await api.get(url);
            setAssignments(res.data?.data || []);
        } catch (err) {
            console.error("Error fetching assignments", err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMsg(null);

        if (!form.class_id || !form.section_id || form.subject_ids.length === 0) {
            setMsg({ type: "error", text: "Class, section and subjects are required" });
            return;
        }

        try {
            setLoading(true);
            for (let subId of form.subject_ids) {
                await api.post(addURL, {
                    class_id: form.class_id,
                    section_id: form.section_id,
                    subject_id: subId,
                });
            }

            setMsg({ type: "success", text: "Subjects assigned successfully" });
            fetchAssignments(form.class_id, form.section_id);
            setForm({ class_id: "", section_id: "", subject_ids: [] });
        } catch (err) {
            console.error("Save error", err);
            setMsg({ type: "error", text: err.response?.data?.message || "Failed to save" });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setForm({ class_id: item.class_id, section_id: item.section_id, subject_ids: [item.subject_id] });
        setEditId(item.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this assignment?")) return;
        try {
            const res = await api.post(deleteURL, { id });
            if (res.data?.status) {
                setMsg({ type: "success", text: res.data.message });
                fetchAssignments(form.class_id, form.section_id);
            } else {
                setMsg({ type: "error", text: res.data.message });
            }
        } catch (err) {
            setMsg({ type: "error", text: "Network error" });
        }
    };

    return (
        <div style={{ background: "#f4f6f8", minHeight: "100vh", padding: "30px" }}>
            <main style={{ maxWidth: "1000px", margin: "auto" }}>
                <section style={cardStyle}>
                    <h2 style={titleStyle}>📚 Assign Subjects to Class & Section</h2>

                    {msg && (
                        <div
                            style={{
                                marginBottom: 12,
                                color: msg.type === "error" ? "#cc0000" : "#166534",
                            }}
                        >
                            {msg.text}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div style={formGrid}>
                            {/* Class */}
                            <FormGroup label="Select Class">
                                <Select
                                    options={classes.map((c) => ({ value: c.id, label: c.class_name }))}
                                    value={
                                        form.class_id
                                            ? {
                                                value: form.class_id,
                                                label: classes.find((c) => c.id === form.class_id)?.class_name,
                                            }
                                            : null
                                    }
                                    onChange={(selected) => {
                                        setForm({
                                            ...form,
                                            class_id: selected ? selected.value : "",
                                            section_id: "",
                                            subject_ids: [],
                                        });
                                        fetchSections(selected ? selected.value : null);
                                        setAssignments([]);
                                    }}
                                    placeholder="Choose class..."
                                    isClearable
                                />
                            </FormGroup>

                            {/* Section */}
                            <FormGroup label="Select Section">
                                <Select
                                    options={sections.map((s) => ({ value: s.id, label: s.section_name }))}
                                    value={
                                        form.section_id
                                            ? {
                                                value: form.section_id,
                                                label: sections.find((s) => s.id === form.section_id)?.section_name,
                                            }
                                            : null
                                    }
                                    onChange={(selected) => {
                                        setForm({ ...form, section_id: selected ? selected.value : "" });
                                        fetchAssignments(form.class_id, selected ? selected.value : null);
                                    }}
                                    placeholder="Choose section..."
                                    isDisabled={!form.class_id}
                                    isClearable
                                />
                            </FormGroup>

                            {/* Subjects */}
                            <FormGroup label="Select Subjects">
                                <Select
                                    options={subjects.map((sub) => ({
                                        value: sub.id,
                                        label: sub.subject_name,
                                    }))}
                                    value={subjects
                                        .filter((sub) => form.subject_ids.includes(sub.id))
                                        .map((sub) => ({ value: sub.id, label: sub.subject_name }))}
                                    onChange={(selected) =>
                                        setForm({
                                            ...form,
                                            subject_ids: selected ? selected.map((s) => s.value) : [],
                                        })
                                    }
                                    placeholder="Choose subjects..."
                                    isMulti
                                    isDisabled={!form.section_id}
                                />
                            </FormGroup>
                        </div>

                        <div style={{ textAlign: "right", marginTop: 20 }}>
                            <button type="submit" style={buttonStyle} disabled={loading}>
                                {editId ? "Update Assignment" : "Assign Subjects"}
                            </button>
                        </div>
                    </form>
                </section>

                <section style={cardStyle}>
                    <h2 style={titleStyle}>📋 Assigned Subjects</h2>
                    {assignments.length === 0 ? (
                        <p>No subjects assigned for this class & section.</p>
                    ) : (
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th>Class</th>
                                    <th>Section</th>
                                    <th>Subject</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {assignments.map((item, idx) => (
                                    <tr key={item.id}>
                                        <td>{item.class_name}</td>
                                        <td>{item.section_name}</td>
                                        <td>{item.subject_name}</td>
                                        <td>
                                            <FaEdit
                                                onClick={() => handleEdit(item)}
                                                style={{ color: "#0077cc", cursor: "pointer", marginRight: "12px" }}
                                            />
                                            <FaTrash
                                                onClick={() => handleDelete(item.id)}
                                                style={{ color: "red", cursor: "pointer" }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>
            </main>
        </div>
    );
};

// UI helpers
const FormGroup = ({ label, children }) => (
    <div>
        <label style={{ marginBottom: "6px", display: "block", fontWeight: 500, color: "#444" }}>
            {label}
        </label>
        {children}
    </div>
);

const cardStyle = {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    marginBottom: "25px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
};

const titleStyle = { color: "#0077cc", marginBottom: "20px" };
const buttonStyle = {
    padding: "10px 25px",
    background: "#0077cc",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    cursor: "pointer",
};
const formGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
};
const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "15px",
};
export default ClassSubjectAssignPage;
